import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  connectAuthEmulator, createUserWithEmailAndPassword, getAuth, GoogleAuthProvider,
  linkWithCredential, onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword,
  signInWithPopup, signOut, type Auth, type AuthCredential, type AuthError, type User,
} from "firebase/auth";
import { config } from "../config";

// The SOLE importer of the Firebase SDK in this app — same boundary as customer-web's
// auth/firebase.ts, so tests stub one module and the SDK never leaks into components.
// Staff sign in with email + password or Google (ADR-0058). Accounts are created by
// invite, and a password invitee sets their own password. Google on an email that
// already has a password links onto that user so RoleAssignment keeps its uid.

let app: FirebaseApp | undefined;
let auth: Auth | undefined;

export function getFirebaseAuth(): Auth {
  if (!auth) {
    app = initializeApp({
      apiKey: config.firebase.apiKey,
      authDomain: config.firebase.authDomain,
      projectId: config.firebase.projectId,
    });
    auth = getAuth(app);
    if (config.firebase.authEmulatorUrl) {
      connectAuthEmulator(auth, config.firebase.authEmulatorUrl, { disableWarnings: true });
    }
  }
  return auth;
}

export function subscribeAuth(cb: (user: User | null) => void): () => void {
  return onAuthStateChanged(getFirebaseAuth(), cb);
}

export const firebaseSignInWithPassword = (email: string, password: string) =>
  signInWithEmailAndPassword(getFirebaseAuth(), email, password);

export const firebaseSignOut = () => signOut(getFirebaseAuth());

export type GoogleSignIn = { email: string | null };

// Held between the popup's "account exists" error and the password the user types next.
// The credential is what linkWithCredential needs; the email is which user it belongs to.
let pendingGoogle: { email: string; credential: AuthCredential } | null = null;

export class GoogleLinkRequiredError extends Error {
  readonly code = "auth/account-exists-with-different-credential";
  readonly email: string;

  constructor(email: string) {
    super("Google account exists with a different credential");
    this.name = "GoogleLinkRequiredError";
    this.email = email;
  }
}

export async function firebaseSignInWithGoogle(): Promise<GoogleSignIn> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  try {
    const result = await signInWithPopup(getFirebaseAuth(), provider);
    pendingGoogle = null;
    return { email: result.user.email };
  } catch (err) {
    if ((err as { code?: string })?.code === "auth/account-exists-with-different-credential") {
      const credential = GoogleAuthProvider.credentialFromError(err as AuthError);
      const email = (err as { customData?: { email?: string } }).customData?.email ?? "";
      pendingGoogle = credential ? { email, credential } : null;
      throw new GoogleLinkRequiredError(email);
    }
    throw err;
  }
}

// Signs in as the existing password user, then attaches the pending Google credential
// when it is for this same email. A failed link still leaves that password session:
// RoleAssignment is stored against this uid, which is the session that matters.
export async function firebaseLinkGoogleWithPassword(email: string, password: string): Promise<void> {
  const signedIn = await signInWithEmailAndPassword(getFirebaseAuth(), email, password);
  const pending = pendingGoogle;
  pendingGoogle = null;
  if (!pending?.credential) return;
  if (pending.email.trim().toLowerCase() !== email.trim().toLowerCase()) return;
  try {
    await linkWithCredential(signedIn.user, pending.credential);
  } catch {
    // The password sign-in above is the account the role is bound to.
  }
}

// An invitee sets their own password when accepting an invite — the account is created
// here, in the browser, and the API then binds the resulting uid to the invite.
export const firebaseCreateUser = (email: string, password: string) =>
  createUserWithEmailAndPassword(getFirebaseAuth(), email, password);

export const firebaseSendPasswordReset = (email: string) =>
  sendPasswordResetEmail(getFirebaseAuth(), email);
